import clsx from 'clsx';
import { CircleAlert, Download, ExternalLink, FileSpreadsheet, FileText, Paperclip, Trash2, X } from 'lucide-react';
import { useCallback, useRef, useState, type DragEvent } from 'react';
import { ApiError, uploadFile, type UploadedFile } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { formatBytes, formatDate } from '../lib/format';
import { Button, buttonClass } from './ui';

/**
 * Finance's evidence files (Finance Phase 1, 2026-10-28): the legacy Excel or
 * PDF behind a historical month, the statements behind the opening position.
 * Each file uploads the moment it is picked — the three-step upload — and the
 * record then takes them by id. The API checks the type, the name and the first
 * bytes again, so a renamed file is turned away there too.
 */

export interface EvidencePolicy {
  extensions: readonly string[];
  types: readonly string[];
}

const EXCEL = ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];

/** A historical month's legacy file: Excel or PDF (domain/file-policy.ts HISTORICAL_EVIDENCE_TYPES). */
export const HISTORICAL_EVIDENCE: EvidencePolicy = {
  extensions: ['.pdf', '.xls', '.xlsx'],
  types: ['application/pdf', ...EXCEL],
};

/** The opening position's evidence: the same, or a picture of a statement. */
export const FINANCE_EVIDENCE: EvidencePolicy = {
  extensions: [...HISTORICAL_EVIDENCE.extensions, '.png', '.jpg', '.jpeg', '.gif', '.webp'],
  types: [...HISTORICAL_EVIDENCE.types, 'image/png', 'image/jpeg', 'image/gif', 'image/webp'],
};

function allowed(file: File, policy: EvidencePolicy): boolean {
  const extension = /\.[^.]+$/.exec(file.name.toLowerCase())?.[0] ?? '';
  // A browser on Windows may not know a workbook's type; the API decides by its bytes.
  return policy.extensions.includes(extension) && (file.type === '' || policy.types.includes(file.type) || extension === '.xls');
}

const isSheet = (mime: string) => EXCEL.includes(mime);

export type EvidenceUpload = ReturnType<typeof useEvidenceUpload>;

/** The files picked for a record that is not saved yet, uploaded as they come. */
export function useEvidenceUpload(policy: EvidencePolicy) {
  const { t } = useI18n();
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [uploading, setUploading] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const describe = useCallback(
    (e: unknown, name: string): string => {
      if (e instanceof ApiError) {
        if (e.code === 'FILE_TOO_LARGE') {
          const mb = (e.details as { maxMb?: number } | undefined)?.maxMb;
          return t('evidence.tooLarge', { name, mb: mb ?? '' });
        }
        if (e.code === 'FILE_TYPE_NOT_ALLOWED') return t('evidence.wrongType', { name, types: policy.extensions.join(' ') });
      }
      return t('evidence.failed', { name, reason: e instanceof Error ? e.message : String(e) });
    },
    [policy, t],
  );

  const add = useCallback(
    async (list: FileList | File[]) => {
      setError(null);
      for (const file of Array.from(list)) {
        if (!allowed(file, policy)) {
          setError(t('evidence.wrongType', { name: file.name, types: policy.extensions.join(' ') }));
          continue;
        }
        setUploading((u) => [...u, file.name]);
        try {
          const uploaded = await uploadFile(file, file.name);
          setFiles((f) => [...f, uploaded]);
        } catch (e) {
          setError(describe(e, file.name));
        } finally {
          setUploading((u) => u.filter((n) => n !== file.name));
        }
      }
    },
    [describe, policy, t],
  );

  const remove = useCallback((id: string) => setFiles((f) => f.filter((file) => file.id !== id)), []);
  const clear = useCallback(() => {
    setFiles([]);
    setError(null);
  }, []);

  return { files, ids: files.map((f) => f.id), busy: uploading.length > 0, uploading, error, add, remove, clear };
}

/** Pick or drop files; each shows as it uploads, and can be taken off before saving. */
export function EvidencePicker({ upload, policy }: { upload: EvidenceUpload; policy: EvidencePolicy }) {
  const { t, locale } = useI18n();
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length) void upload.add(e.dataTransfer.files);
  };

  return (
    <div>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={clsx(
          'flex flex-wrap items-center gap-3 rounded-md border border-dashed px-4 py-3',
          dragging ? 'border-primary bg-primary/5' : 'hairline',
        )}
      >
        <Paperclip className="h-4 w-4 text-[var(--text-tertiary)]" aria-hidden />
        <span className="text-body-sm text-muted">{t('evidence.drop', { types: policy.extensions.join(' ') })}</span>
        <Button size="sm" onClick={() => input.current?.click()}>
          {t('evidence.choose')}
        </Button>
        <input
          ref={input}
          type="file"
          multiple
          hidden
          accept={[...policy.extensions, ...policy.types].join(',')}
          onChange={(e) => {
            if (e.target.files?.length) void upload.add(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {(upload.files.length > 0 || upload.uploading.length > 0) && (
        <ul className="mt-3 space-y-1.5">
          {upload.files.map((file) => (
            <li key={file.id} className="flex items-center gap-2 text-body-sm">
              {isSheet(file.mimeType) ? (
                <FileSpreadsheet className="h-4 w-4 shrink-0 text-success-fg dark:text-success" aria-hidden />
              ) : (
                <FileText className="h-4 w-4 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
              )}
              <span className="min-w-0 truncate" dir="auto">{file.originalName}</span>
              <span className="text-caption text-faint tabular">{formatBytes(file.sizeBytes, locale)}</span>
              <button
                type="button"
                onClick={() => upload.remove(file.id)}
                className="ms-auto rounded p-1 text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                aria-label={t('common.remove')}
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </li>
          ))}
          {upload.uploading.map((name) => (
            <li key={name} className="flex items-center gap-2 text-body-sm text-muted">
              <FileText className="h-4 w-4 shrink-0 animate-pulse" aria-hidden />
              <span className="truncate" dir="auto">{name}</span>
              <span className="text-caption">{t('evidence.uploading')}</span>
            </li>
          ))}
        </ul>
      )}

      {upload.error && (
        <p className="mt-2 flex items-start gap-1.5 text-caption text-error-fg dark:text-error-dark" role="alert">
          <CircleAlert className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          {upload.error}
        </p>
      )}
    </div>
  );
}

export interface AttachedFile {
  id: string;
  createdAt: string;
  file: UploadedFile & { uploadedBy?: { fullName: string; fullNameAr?: string | null } | null };
}

/**
 * Files already on a record: open in a tab of their own (a PDF shows, a
 * workbook downloads), download, and — with the right — take off, after a second
 * click rather than a popup.
 */
export function AttachedFiles({
  files,
  onRemove,
  removing,
}: {
  files: AttachedFile[];
  onRemove?: (attachmentId: string) => void;
  removing?: string | null;
}) {
  const { t, locale, localized } = useI18n();
  const [confirming, setConfirming] = useState<string | null>(null);

  if (files.length === 0) return <p className="text-body-sm text-muted">{t('evidence.none')}</p>;

  return (
    <ul className="divide-y hairline">
      {files.map(({ id, file, createdAt }) => (
        <li key={id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5">
          {isSheet(file.mimeType) ? (
            <FileSpreadsheet className="h-5 w-5 shrink-0 text-success-fg dark:text-success" aria-hidden />
          ) : (
            <FileText className="h-5 w-5 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-body-sm text-[var(--text-primary)]" dir="auto">{file.originalName}</p>
            <p className="text-caption text-faint">
              <span className="tabular">{formatBytes(file.sizeBytes, locale)}</span> · {formatDate(createdAt, locale)}
              {file.uploadedBy ? ` · ${localized({ fullName: file.uploadedBy.fullName, fullNameAr: file.uploadedBy.fullNameAr })}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <a href={file.url} target="_blank" rel="noreferrer" className={buttonClass('ghost', 'sm')} title={t('evidence.open')}>
              <ExternalLink className="h-4 w-4" aria-hidden />
              <span className="sr-only">{t('evidence.open')}</span>
            </a>
            <a href={file.downloadUrl} className={buttonClass('ghost', 'sm')} title={t('evidence.download')}>
              <Download className="h-4 w-4" aria-hidden />
              <span className="sr-only">{t('evidence.download')}</span>
            </a>
            {onRemove &&
              (confirming === id ? (
                <Button size="sm" variant="danger" loading={removing === id} onClick={() => onRemove(id)}>
                  {t('evidence.removeConfirm')}
                </Button>
              ) : (
                <Button size="sm" variant="ghost" onClick={() => setConfirming(id)} title={t('common.remove')}>
                  <Trash2 className="h-4 w-4" aria-hidden />
                  <span className="sr-only">{t('common.remove')}</span>
                </Button>
              ))}
          </div>
        </li>
      ))}
    </ul>
  );
}
